// mapMarkers.js — the Desk map's marker layer, kept by IDENTITY (audit 2026-10-23, Desk map section,
// performance priority 3).
//
// Until this, EntriesMapView removed its whole cluster layer and built a new one whenever the loaded pins
// or the corpus dots changed. Those arrays change on every page that lands: a loaded group's teardrop
// replaces its own corpus dot, so ONE promoted group rebuilt every remaining dot as well. The audit's
// exact-effect mock measured it: 1,000 markers, one promotion, 1,000 new marker objects (a logical call
// count, not a device timing). Here each marker has a stable key — `g:<group key>` for a loaded group,
// `c:<listing id>` for a corpus dot — and a change of input adds, removes, moves, re-icons or re-titles only
// the markers whose key or facts changed. The layer itself lives as long as the map.
//
// Pure apart from the `ops` it is handed: the component passes Leaflet (leafletMarkerOps below), the test
// passes the audit's in-memory stub, and both run this same code.

/**
 * One spec per marker the map should show. Loaded pins first: a dot whose key a loaded pin already holds is
 * dropped (the caller removes loaded ids from the corpus too; this keeps one marker per key regardless).
 * `titleOf(group)` is the loaded pin's tooltip text.
 */
export function markerSpecs(pins, bgPins, titleOf) {
  const out = []
  const seen = new Map()
  for (const p of pins || []) {
    const g = p.group
    const lid = g?.items?.[0]?.listing?.listing_id || g?.items?.[0]?.result?.listing_id
    let key = 'g:' + (g?.key ?? lid ?? `${p.coords.lat},${p.coords.lng}`)
    // Two groups sharing a key would otherwise fight over one marker; the second gets its own.
    const n = seen.get(key) || 0
    seen.set(key, n + 1)
    if (n) key += '#' + n
    out.push({ key, kind: 'pin', lat: p.coords.lat, lng: p.coords.lng, source: p.coords.source ?? null,
               title: titleOf ? titleOf(g) : '', group: g })
  }
  for (const d of bgPins || []) {
    const [id, lat, lng] = d
    const key = 'c:' + id
    if (seen.has(key)) continue
    seen.set(key, 1)
    out.push({ key, kind: 'dot', id, lat, lng })
  }
  return out
}

/**
 * Bring `layer` and `state` (Map key → { marker, spec }) to `specs`. Returns what it did, for tests and
 * for anyone measuring. Handlers bound by `ops.create` must read `entry.spec` at event time — the entry
 * object is kept across syncs and its spec is replaced, so a click always opens the CURRENT group.
 */
export function syncMarkerLayer(layer, state, specs, ops) {
  const stats = { created: 0, removed: 0, moved: 0, reiconed: 0, retitled: 0, kept: 0 }
  const want = new Set()
  const toAdd = [], toRemove = []
  for (const s of specs) {
    want.add(s.key)
    const e = state.get(s.key)
    if (e && e.spec.kind !== s.kind) {   // a key never changes kind today; if it did, start again
      toRemove.push(e.marker)
      state.delete(s.key)
      stats.removed++
    } else if (e) {
      const prev = e.spec
      e.spec = s
      let touched = false
      if (prev.lat !== s.lat || prev.lng !== s.lng) { ops.move(e); stats.moved++; touched = true }
      if (s.kind === 'pin' && prev.source !== s.source) { ops.setIcon(e); stats.reiconed++; touched = true }
      if (s.kind === 'pin' && prev.title !== s.title) { ops.retitle(e); stats.retitled++; touched = true }
      if (!touched) stats.kept++
      continue
    }
    const entry = { marker: null, spec: s }
    entry.marker = ops.create(entry)
    state.set(s.key, entry)
    toAdd.push(entry.marker)
    stats.created++
  }
  for (const [key, e] of state) {
    if (want.has(key)) continue
    toRemove.push(e.marker)
    state.delete(key)
    stats.removed++
  }
  // markercluster's bulk calls re-cluster once per batch instead of once per marker.
  if (toRemove.length) {
    if (typeof layer.removeLayers === 'function') layer.removeLayers(toRemove)
    else for (const m of toRemove) layer.removeLayer(m)
  }
  if (toAdd.length) {
    if (typeof layer.addLayers === 'function') layer.addLayers(toAdd)
    else for (const m of toAdd) layer.addLayer(m)
  }
  return stats
}

/**
 * The Leaflet half. `onPin(group)`, `onDot(id)` and `onDotHover(marker, id)` are read at event time, so the
 * caller passes functions that consult refs — the markers outlive any one render's props.
 */
export function leafletMarkerOps({ L, pinIcon, dotIcon, onPin, onDot, onDotHover }) {
  return {
    create(entry) {
      const s = entry.spec
      if (s.kind === 'pin') {
        const m = L.marker([s.lat, s.lng], { icon: pinIcon(s.source) })
        m.bindTooltip(s.title)
        m.on('click', () => onPin(entry.spec.group))
        return m
      }
      const m = L.marker([s.lat, s.lng], { icon: dotIcon })
      m.on('click', () => onDot(entry.spec.id))
      if (onDotHover) m.on('mouseover', () => onDotHover(m, entry.spec.id))
      return m
    },
    move(entry) { entry.marker.setLatLng([entry.spec.lat, entry.spec.lng]) },
    setIcon(entry) { entry.marker.setIcon(pinIcon(entry.spec.source)) },
    retitle(entry) {
      const m = entry.marker
      if (typeof m.getTooltip === 'function' && m.getTooltip()) m.setTooltipContent(entry.spec.title)
      else m.bindTooltip(entry.spec.title)
    },
  }
}
